import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { CompanyProfile } from '@/lib/companyProfile';
import type { FinanceInvoice, FinanceInvoiceItem } from '@/lib/finance';
import { emailInvoice, printInvoice, shareInvoiceWhatsApp } from '@/lib/invoiceShare';

type Action = 'print' | 'whatsapp' | 'email';

/**
 * Bouton « Envoyer / Imprimer » d'une facture ou d'un devis.
 * Ouvre un menu avec impression PDF, partage WhatsApp du PDF et e-mail.
 */
export default function InvoiceShareMenu({
  invoice,
  profile,
  onNote,
}: {
  invoice: FinanceInvoice;
  profile: CompanyProfile;
  onNote?: (message: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<Action | null>(null);
  const itemsRef = useRef<FinanceInvoiceItem[] | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Préchargement des lignes : indispensable pour que le partage natif
  // s'exécute encore dans le geste utilisateur (iOS Safari).
  useEffect(() => {
    if (!open || itemsRef.current) return;
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from('finance_invoice_items')
        .select('*')
        .eq('invoice_id', invoice.id);
      if (alive) itemsRef.current = (data || []) as FinanceInvoiceItem[];
    })();
    return () => {
      alive = false;
    };
  }, [open, invoice.id]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const loadItems = async (): Promise<FinanceInvoiceItem[]> => {
    if (itemsRef.current) return itemsRef.current;
    const { data } = await supabase
      .from('finance_invoice_items')
      .select('*')
      .eq('invoice_id', invoice.id);
    itemsRef.current = (data || []) as FinanceInvoiceItem[];
    return itemsRef.current;
  };

  const run = async (action: Action) => {
    setBusy(action);
    onNote?.(null);
    try {
      const items = await loadItems();
      if (action === 'print') {
        printInvoice(invoice, items, profile);
      } else if (action === 'whatsapp') {
        const result = await shareInvoiceWhatsApp(invoice, items, profile);
        if (result === 'whatsapp') {
          onNote?.('WhatsApp ouvert — le PDF a été téléchargé pour être joint à votre message.');
        }
      } else {
        emailInvoice(invoice, items, profile);
        onNote?.('Votre messagerie s’ouvre — le PDF a été téléchargé pour être joint au message.');
      }
      setOpen(false);
    } catch {
      onNote?.('Action impossible sur cet appareil. Réessayez depuis un ordinateur.');
    } finally {
      setBusy(null);
    }
  };

  const options: { key: Action; label: string; hint: string; icon: string }[] = [
    { key: 'print', label: 'Imprimer / PDF', hint: 'Ouvre le document prêt à imprimer', icon: 'ri-printer-line' },
    { key: 'whatsapp', label: 'Partager sur WhatsApp', hint: 'Envoie le PDF au client', icon: 'ri-whatsapp-line' },
    { key: 'email', label: 'Envoyer par e-mail', hint: 'Brouillon avec le PDF téléchargé', icon: 'ri-mail-send-line' },
  ];

  return (
    <div className="relative flex-shrink-0" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2.5 h-7 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
          open ? 'bg-primary-50 text-primary-700' : 'text-foreground-500 hover:text-primary-600 hover:bg-primary-50'
        }`}
        title="Envoyer / Imprimer"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <i className={busy ? 'ri-loader-4-line animate-spin text-sm' : 'ri-send-plane-line text-sm'}></i>
        <span className="hidden md:inline">Envoyer</span>
        <i className="ri-arrow-down-s-line text-xs"></i>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1.5 z-40 w-64 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
        >
          <p className="px-3 pt-2.5 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
            Envoyer / Imprimer
          </p>
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              role="menuitem"
              onClick={() => run(opt.key)}
              disabled={busy != null}
              className="w-full flex items-start gap-2.5 px-3 py-2 text-left hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
            >
              <span className="w-7 h-7 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                <i className={`${opt.icon} text-sm`}></i>
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-medium text-foreground-900">{opt.label}</span>
                <span className="block text-[11px] text-foreground-400 leading-tight">{opt.hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}